import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, Mail, ShieldOff, ShieldCheck, Building2, Tag, Pencil, Ban, UserX, Loader2,
  ChevronDown, Check, Send, X, Users, Trash2,
} from 'lucide-react'
import {
  serverAdminApi,
  peopleApi,
  departmentApi,
  type ServerMemberRow,
  type ServerMemberStatus,
  type ServerMemberSort,
  type ServerMemberPageResponse,
} from '../../../services/api'
import { mediaUrl } from '../../../services/config'
import { useToastStore } from '../../../stores/toastStore'
import { PersonProfile } from '@os/people/web/pages/workspace/people/PersonProfile'
import { buildOrgChart } from '@os/people/web/pages/workspace/people/buildOrgChart'
import { rebuildOrgChart } from '@os/people/web/pages/workspace/people/orgChartUtils'
import { peopleRowFromServerMember, serverMemberToPeopleRow, memberDepartments, MAX_MEMBER_DEPARTMENTS } from './memberAdapters'
import { TablePanel, DataTable, type Column } from '../../../components/patterns'
import { dialog } from '../../../services/dialog'

interface Department {
  id: string
  name: string
}
interface RoleSummary {
  id: string
  name: string
  color: string
}

const STATUS_OPTIONS: { value: ServerMemberStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'INVITED', label: 'Invited' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DISABLED', label: 'Disabled' },
]

const STATUS_TONE: Record<string, string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  INVITED: 'bg-amber-500/15 text-amber-500',
  ACTIVE: 'bg-emerald-500/15 text-emerald-500',
  DISABLED: 'bg-rose-500/15 text-rose-500',
}

const PAGE_SIZES = [25, 50, 100, 200]

/**
 * Friendly "last seen" formatter. Inside 24h → relative ("5m ago",
 * "3h ago"); inside 7d → weekday + time; older → date + time.
 * Tooltip on hover gets the full ISO so an admin can still copy the
 * exact moment.
 */
function formatLastSeen(iso: string | null | undefined): { short: string; full: string } {
  if (!iso) return { short: 'never', full: 'Never seen' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { short: '—', full: String(iso) }
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffH = Math.floor(diffMs / 3_600_000)
  const diffD = Math.floor(diffMs / 86_400_000)
  const full = d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  let short: string
  if (diffMin < 1) short = 'just now'
  else if (diffMin < 60) short = `${diffMin}m ago`
  else if (diffH < 24) short = `${diffH}h ago`
  else if (diffD < 7) short = `${diffD}d ago`
  else short = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  return { short, full }
}

/**
 * Rewritten Members tab for ServerAdminPage.
 *
 * Replaces the previous all-in-memory table with:
 *   - server-side paginated search + filter (status, dept, role)
 *   - checkbox column + bulk action bar (re-invite, disable, enable,
 *     change department, add/remove role)
 *   - per-row click opens the shared PersonProfile drawer (the same
 *     one wired into /workspace/people), in view mode with its own
 *     inline Edit toggle — ONE profile surface across the app
 *   - 50/page default, page-size selector + page navigation
 *
 * Department dropdown stays inline for the common "fix one row's
 * dept" case; for many rows, use the bulk bar.
 */
export function MembersTab({
  serverId,
  departments,
  roles,
}: {
  serverId: string
  departments: Department[]
  roles: RoleSummary[]
}) {
  const showToast = useToastStore((s) => s.showToast)

  /* ── Query state ──────────────────────────────────────────── */
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<ServerMemberStatus>('all')
  const [deptFilter, setDeptFilter] = useState<string>('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [sort, setSort] = useState<ServerMemberSort>('name')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  /* ── Data + loading ──────────────────────────────────────── */
  const [data, setData] = useState<ServerMemberPageResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  // Memo of who's selected from the CURRENT page; cleared across page
  // moves so the bulk bar never claims phantom selections from a list
  // the admin can't see.

  // Manager (reports-to) candidates for the edit modal — every member of
  // THIS server, sourced from the org endpoint pinned to serverId so the
  // list is complete regardless of the members-table pagination + the
  // active-server header. Mirrors the People page's manager dropdown.
  const [managerOptions, setManagerOptions] = useState<{ id: string; name: string }[]>([])
  useEffect(() => {
    let cancelled = false
    peopleApi.org(serverId)
      .then((r) => {
        if (cancelled) return
        setManagerOptions(
          r.data.people.map((p) => ({
            id: p.id,
            name: [p.firstName, p.lastName].filter(Boolean).join(' ').trim() || p.email,
          })),
        )
        // Also populate the shared org-chart singleton so PersonProfile's
        // getNode(userId) resolves in the drawer. Without this the drawer
        // renders empty (view mode does `if (!getNode(personId)) return null`),
        // since that singleton is otherwise only filled by the People page's
        // useDirectory. It's a shared singleton, but the People route and
        // Server-Admin aren't mounted at once, and the People page rebuilds it
        // on its own mount — same pattern os-people's useDirectory.ts uses.
        const { nodes, rootId } = buildOrgChart(r.data)
        rebuildOrgChart(nodes, rootId)
      })
      .catch(() => { if (!cancelled) setManagerOptions([]) })
    return () => { cancelled = true }
  }, [serverId])

  const fetchOnce = useCallback(async () => {
    setLoading(true)
    try {
      const res = await serverAdminApi.membersPage(serverId, {
        q: q.trim() || undefined,
        status,
        deptId: deptFilter || undefined,
        roleId: roleFilter || undefined,
        page,
        pageSize,
        sort,
      })
      setData(res.data)
    } catch (e: any) {
      showToast({
        id: `members-fetch-${Date.now()}`,
        type: 'error',
        title: 'Failed to load members',
        body: e?.response?.data?.error?.toString?.() || String(e),
      })
    } finally {
      setLoading(false)
    }
  }, [serverId, q, status, deptFilter, roleFilter, page, pageSize, sort, showToast])

  // Debounce search input. Filters apply immediately on selection.
  const debounceRef = useRef<number | null>(null)
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => { setPage(1); fetchOnce() }, 250)
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q])

  useEffect(() => { setPage(1); fetchOnce() }, [status, deptFilter, roleFilter, sort, pageSize]) // eslint-disable-line
  useEffect(() => { fetchOnce() }, [page]) // eslint-disable-line

  /* ── Selection helpers ───────────────────────────────────── */
  const pageRows = data?.members ?? []
  const pageUserIds = useMemo(() => pageRows.map((m) => m.userId), [pageRows])
  const allSelectedOnPage = pageUserIds.length > 0 && pageUserIds.every((id) => selected.has(id))
  const someSelectedOnPage = pageUserIds.some((id) => selected.has(id)) && !allSelectedOnPage

  const togglePageSelection = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelectedOnPage) {
        for (const id of pageUserIds) next.delete(id)
      } else {
        for (const id of pageUserIds) next.add(id)
      }
      return next
    })
  }
  const toggleRowSelection = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }
  const clearSelection = () => setSelected(new Set())
  const selectedCount = selected.size

  /* ── Inline single-row mutations ─────────────────────────── */
  // OSPROD-275: a member can sit in up to MAX_MEMBER_DEPARTMENTS teams. The
  // FIRST id is the primary — org chart, approver routing and the sidebar
  // grouping all read it, so the order of these selects is meaningful, not
  // cosmetic.
  const assignDepartments = async (userId: string, departmentIds: string[]) => {
    try {
      await departmentApi.assignMemberDepartments(serverId, userId, departmentIds)
      fetchOnce()
    } catch (e: any) {
      showToast({
        id: `dept-set-${Date.now()}`,
        type: 'error',
        title: errMessage(e) || 'Failed to set department',
      })
    }
  }
  /** Replace the department at `index`; an empty value removes it. */
  const setDepartmentAt = (m: { userId: string; departmentIds?: string[] | null; departmentId?: string | null }, index: number, value: string) => {
    const current = memberDepartments(m)
    const next = value ? [...current] : current.filter((_, i) => i !== index)
    if (value) next[index] = value
    // Drop duplicates — picking a team the member already has would otherwise
    // silently consume one of their three slots.
    void assignDepartments(m.userId, [...new Set(next.filter(Boolean))])
  }

  /* ── Bulk actions ────────────────────────────────────────── */
  // `op` returns the success-toast title (so each action computes its
  // own summary from the API response). The previous shape took a
  // separate successTitle string AND let `op` raise its own toast,
  // which meant bulkReInvite emitted both a "Re-invited X • skipped Y"
  // toast AND an empty-title "" toast right after.
  const runBulk = async (op: () => Promise<string>) => {
    try {
      const title = await op()
      if (title) showToast({ id: `bulk-${Date.now()}`, type: 'message', title })
      clearSelection()
      fetchOnce()
    } catch (e: any) {
      showToast({
        id: `bulk-err-${Date.now()}`,
        type: 'error',
        title: errMessage(e) || 'Bulk action failed',
      })
    }
  }

  const bulkReInvite = () => runBulk(async () => {
    const res = await peopleApi.bulkInvite([...selected])
    return `Re-invited ${res.data.invited} • skipped ${res.data.skipped}`
  })

  const bulkDisable = () => runBulk(async () => {
    const n = selected.size
    await peopleApi.bulkDisable([...selected])
    return `Disabled ${n} member${n === 1 ? '' : 's'}`
  })

  const bulkEnable = () => runBulk(async () => {
    const n = selected.size
    await peopleApi.bulkEnable([...selected])
    return `Re-enabled ${n} member${n === 1 ? '' : 's'}`
  })

  const bulkSetDepartment = (departmentId: string | null) => runBulk(async () => {
    const n = selected.size
    await serverAdminApi.bulkDepartment(serverId, { userIds: [...selected], departmentId })
    return `Department updated for ${n} member${n === 1 ? '' : 's'}`
  })

  const bulkAddRole = (roleId: string) => runBulk(async () => {
    const n = selected.size
    await serverAdminApi.bulkRoles(serverId, { userIds: [...selected], addRoleIds: [roleId] })
    return `Role added to ${n} member${n === 1 ? '' : 's'}`
  })

  const bulkRemoveRole = (roleId: string) => runBulk(async () => {
    const n = selected.size
    await serverAdminApi.bulkRoles(serverId, { userIds: [...selected], removeRoleIds: [roleId] })
    return `Role removed from ${n} member${n === 1 ? '' : 's'}`
  })

  const bulkDelete = async () => {
    const n = selected.size
    if (n === 0) return
    if (!(await dialog.confirm({
      message: `Delete ${n} member${n === 1 ? '' : 's'}? They'll be signed out and their email freed for reuse. An admin can restore them later.`,
      tone: 'destructive',
      confirmLabel: 'Delete',
    }))) return
    runBulk(async () => {
      const res = await peopleApi.bulkDelete([...selected])
      return `Deleted ${res.data.deleted}${res.data.skipped ? ` • skipped ${res.data.skipped}` : ''}`
    })
  }

  const deleteMember = async (m: ServerMemberRow) => {
    const u = m.user
    const name = u ? (`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email) : m.userId
    if (!(await dialog.confirm({
      message: `Delete ${name}? They'll be signed out and their email freed for reuse. An admin can restore them later.`,
      tone: 'destructive',
      confirmLabel: 'Delete',
    }))) return
    try {
      const res = await peopleApi.bulkDelete([m.userId])
      showToast({
        id: `del-${Date.now()}`,
        type: res.data.deleted ? 'message' : 'error',
        title: res.data.deleted ? 'Member deleted' : 'Not deleted (skipped — admin or already removed)',
      })
      fetchOnce()
    } catch (e: any) {
      showToast({ id: `del-err-${Date.now()}`, type: 'error', title: errMessage(e) || 'Failed to delete member' })
    }
  }

  /* ── Per-row view/edit drawer (shared PersonProfile) ─────── */
  const [viewingMember, setViewingMember] = useState<ServerMemberRow | null>(null)

  /* ── Per-row send-invite single-shot ─────────────────────── */
  const sendInvite = async (userId: string) => {
    try {
      const res = await peopleApi.sendInvite(userId)
      showToast({
        id: `invite-${Date.now()}`,
        type: 'message',
        title: res.data.emailSent ? 'Invite sent' : 'Status updated — email may not have been delivered',
      })
      fetchOnce()
    } catch (e: any) {
      // Surface the server's exact reason ("User is already active" /
      // "Fill in a real email…") instead of stacking a generic
      // "Failed to send invite" header AND a separate body toast.
      showToast({
        id: `invite-err-${Date.now()}`,
        type: 'error',
        title: errMessage(e) || 'Failed to send invite',
      })
    }
  }

  /* ── UI ──────────────────────────────────────────────────── */
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const statusCounts = data?.statusCounts ?? {}

  const memberColumns: Column<ServerMemberRow>[] = [
    {
      key: 'select',
      width: '2.5rem',
      header: (
        <input
          type="checkbox"
          ref={(el) => { if (el) el.indeterminate = someSelectedOnPage }}
          checked={allSelectedOnPage}
          onChange={togglePageSelection}
          className="rounded border-border"
          aria-label="Select all on page"
        />
      ),
      render: (m) => (
        <input
          type="checkbox"
          checked={selected.has(m.userId)}
          onChange={() => toggleRowSelection(m.userId)}
          className="rounded border-border"
          aria-label="Select member"
        />
      ),
    },
    {
      key: 'member', header: 'Member',
      render: (m) => {
        const u = m.user
        const fullName = u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : m.userId
        return (
          <button onClick={() => setViewingMember(m)} className="flex items-center gap-2 text-left hover:opacity-80 transition">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
              {u?.avatarUrl ? <img src={mediaUrl(u.avatarUrl)} alt="" className="w-full h-full object-cover" /> : (fullName[0] ?? '?').toUpperCase()}
            </div>
            <span className="text-foreground font-medium underline-offset-2 hover:underline truncate max-w-[180px]">{fullName}</span>
          </button>
        )
      },
    },
    { key: 'position', header: 'Position', render: (m) => <span className="text-muted-foreground truncate max-w-[160px] inline-block align-middle">{m.user?.position || '—'}</span> },
    { key: 'email', header: 'Email', render: (m) => <span className="text-muted-foreground truncate max-w-[200px] inline-block align-middle">{m.user?.email}</span> },
    {
      key: 'department', header: 'Departments',
      render: (m) => {
        const assigned = memberDepartments(m)
        // Always render one select (the primary slot) even when empty, so the
        // control is where it has always been for members with no department.
        const slots = assigned.length > 0 ? assigned : ['']
        return (
          <div className="flex flex-col gap-1">
            {slots.map((deptId, i) => (
              <select
                key={`${m.userId}-dept-${i}`}
                className="bg-background text-foreground text-xs rounded border border-border px-2 py-1 min-w-[140px] max-w-[200px] focus:outline-none focus:ring-1 focus:ring-primary"
                value={deptId}
                onChange={(e) => setDepartmentAt(m, i, e.target.value)}
                title={i === 0 ? 'Primary department — drives the org chart and approvals' : 'Additional department'}
              >
                <option value="">— None —</option>
                {departments.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
              </select>
            ))}
            {assigned.length > 0 && assigned.length < MAX_MEMBER_DEPARTMENTS && (
              <button
                onClick={() => void assignDepartments(m.userId, [...assigned, departments.find((d) => !assigned.includes(d.id))?.id ?? ''].filter(Boolean))}
                className="text-[11px] text-primary hover:underline text-left"
                disabled={departments.every((d) => assigned.includes(d.id))}
              >
                + Add department
              </button>
            )}
          </div>
        )
      },
    },
    {
      key: 'roles', header: 'Roles',
      render: (m) => (
        <div className="flex flex-wrap gap-1 max-w-[180px]">
          {m.roles.length === 0 && <span className="text-xs text-muted-foreground/60">—</span>}
          {m.roles.map((mr) => (
            <span key={mr.id} className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: `${mr.role.color}1f`, color: mr.role.color }}>{mr.role.name}</span>
          ))}
        </div>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (m) => m.user ? (
        <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${STATUS_TONE[m.user.accountStatus] ?? ''}`}>{m.user.accountStatus}</span>
      ) : null,
    },
    {
      key: 'lastSeen', header: 'Last seen',
      render: (m) => {
        const ls = formatLastSeen(m.user?.lastSeenAt)
        return <span title={ls.full} className="tabular-nums text-xs whitespace-nowrap">{ls.short}</span>
      },
    },
    {
      key: 'actions', header: '', align: 'right',
      render: (m) => (
        <span className="whitespace-nowrap">
          {m.user && m.user.accountStatus !== 'ACTIVE' && (
            <button onClick={() => sendInvite(m.userId)} className="text-muted-foreground hover:text-primary p-1.5 transition" title="Send invite"><Mail size={14} /></button>
          )}
          <button onClick={() => setViewingMember(m)} className="text-muted-foreground hover:text-foreground p-1.5 transition" title="Edit"><Pencil size={14} /></button>
          <button onClick={() => deleteMember(m)} className="text-muted-foreground hover:text-destructive p-1.5 transition" title="Delete member"><Trash2 size={14} /></button>
        </span>
      ),
    },
  ]

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-[400px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, position…"
            className="w-full bg-muted ring-1 ring-border rounded pl-9 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-primary transition"
          />
          {q && (
            <button
              onClick={() => setQ('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <Select value={deptFilter} onChange={setDeptFilter}>
          <option value="">All departments</option>
          <option value="none">— Unassigned —</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
        <Select value={roleFilter} onChange={setRoleFilter}>
          <option value="">All roles</option>
          <option value="none">— No role —</option>
          {roles.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </Select>
        <Select value={sort} onChange={(v) => setSort(v as ServerMemberSort)}>
          <option value="name">Sort: name</option>
          <option value="joinedAt">Sort: joined</option>
          <option value="lastSeen">Sort: last seen</option>
        </Select>
      </div>

      {/* Status chip strip */}
      <div className="flex flex-wrap gap-1.5">
        {STATUS_OPTIONS.map((s) => {
          const count = s.value === 'all'
            ? Object.values(statusCounts).reduce<number>((a, b) => a + (b ?? 0), 0)
            : (statusCounts as any)[s.value] ?? 0
          const active = status === s.value
          return (
            <button
              key={s.value}
              onClick={() => setStatus(s.value)}
              className={`text-xs px-2.5 py-1 rounded-full ring-1 transition ${
                active
                  ? 'bg-primary/15 text-primary ring-primary/30'
                  : 'bg-muted text-muted-foreground ring-border hover:text-foreground'
              }`}
            >
              {s.label} <span className="tabular-nums opacity-70">· {count}</span>
            </button>
          )
        })}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 bg-primary/10 ring-1 ring-primary/30 backdrop-blur rounded-lg px-3 py-2">
          <span className="text-xs font-semibold text-primary tabular-nums">
            {selectedCount} selected
          </span>
          <span className="flex-1" />
          <BulkButton icon={<Send size={12} />} label="Re-invite" onClick={bulkReInvite} />
          <BulkButton icon={<ShieldOff size={12} />} label="Disable" onClick={bulkDisable} tone="destructive" />
          <BulkButton icon={<ShieldCheck size={12} />} label="Re-enable" onClick={bulkEnable} />
          <BulkDropdown
            icon={<Building2 size={12} />}
            label="Department"
            options={[
              { value: 'none', label: '— Unassigned —' },
              ...departments.map((d) => ({ value: d.id, label: d.name })),
            ]}
            onPick={(v) => bulkSetDepartment(v === 'none' ? null : v)}
          />
          <BulkDropdown
            icon={<Tag size={12} />}
            label="Add role"
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onPick={bulkAddRole}
          />
          <BulkDropdown
            icon={<Tag size={12} />}
            label="Remove role"
            options={roles.map((r) => ({ value: r.id, label: r.name }))}
            onPick={bulkRemoveRole}
          />
          <BulkButton icon={<Trash2 size={12} />} label="Delete" onClick={bulkDelete} tone="destructive" />
          <button
            onClick={clearSelection}
            className="text-xs px-2 py-1 rounded text-muted-foreground hover:bg-accent/40 hover:text-foreground transition"
          >
            <X size={12} className="inline" /> Clear
          </button>
        </div>
      )}

      <TablePanel
        icon={<Users size={16} />}
        title="Members"
        count={total}
        className="min-h-0 flex-1"
        footer={
          <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground">
            <span className="tabular-nums">{total === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`} of {total}</span>
            <span className="flex-1" />
            <label className="flex items-center gap-1.5">
              Per page:
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)) }} className="bg-background ring-1 ring-border rounded px-2 py-1 text-foreground">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-2 py-1 ring-1 ring-border rounded disabled:opacity-40 hover:bg-accent/40 transition">Prev</button>
            <span className="tabular-nums">page {page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-2 py-1 ring-1 ring-border rounded disabled:opacity-40 hover:bg-accent/40 transition">Next</button>
          </div>
        }
      >
        <DataTable
          columns={memberColumns}
          rows={pageRows}
          rowKey={(m) => m.id}
          empty={
            loading
              ? <div className="py-10 text-center text-muted-foreground"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>
              : <div className="py-10 text-center text-muted-foreground">No members match</div>
          }
        />
      </TablePanel>

      {viewingMember && viewingMember.user && (
        <aside className="fixed right-0 top-0 z-50 h-full w-[380px] border-l border-border bg-card shadow-xl overflow-y-auto">
          <PersonProfile
            personId={viewingMember.userId}
            // Real DirectoryRow from the member payload so view mode shows the
            // right status / hire date / phone / meta instead of the empty-null
            // fallback (which read as DRAFT-for-everyone). aboutMe + managerId
            // aren't carried on the server-admin member payload — null, same as
            // serverMemberToPeopleRow does. departmentName resolves from the
            // server's department list by the member's departmentId.
            directoryRow={{
              empId: viewingMember.userId,
              userId: viewingMember.userId,
              email: viewingMember.user.email,
              firstName: viewingMember.user.firstName,
              lastName: viewingMember.user.lastName,
              avatarUrl: viewingMember.user.avatarUrl,
              accountStatus: viewingMember.user.accountStatus,
              hireDate: viewingMember.user.hireDate,
              position: viewingMember.user.position,
              cellPhone: viewingMember.user.cellPhone,
              locationCountry: viewingMember.user.locationCountry,
              departmentName: departments.find((d) => d.id === viewingMember.departmentId)?.name ?? null,
              lastSeenAt: viewingMember.user.lastSeenAt,
              inviteSentAt: viewingMember.user.inviteSentAt,
              aboutMe: null,
              managerId: null,
            }}
            onClose={() => setViewingMember(null)}
            onJumpTo={() => {}}
            editUser={serverMemberToPeopleRow(viewingMember)}
            save={(id, patch) => peopleApi.update(id, patch).then((r) => ({ data: peopleRowFromServerMember(r.data) }))}
            onSaved={() => { setViewingMember(null); fetchOnce() }}
            managerOptions={managerOptions}
            departmentOptions={departments.map((d) => ({ id: d.id, name: d.name }))}
            currentDepartmentId={viewingMember.departmentId ?? null}
            onSaveDepartment={async (deptId) => {
              // OSPROD-275: this control edits the PRIMARY department only.
              // Routing it through bulkDepartment would replace the member's
              // whole set and silently drop their other teams — keep the
              // extras and just swap the first slot.
              const rest = memberDepartments(viewingMember).filter((d) => d !== deptId).slice(1)
              await departmentApi.assignMemberDepartments(
                serverId,
                viewingMember.userId,
                [deptId, ...rest].filter(Boolean) as string[],
              )
            }}
            serverContext={{
              serverId,
              availableRoles: roles.map((r) => ({ id: r.id, name: r.name, color: r.color })),
              currentRoleIds: viewingMember.roles.map((mr) => mr.role.id),
              saveRoles: async (roleIds: string[]) => {
                await serverAdminApi.bulkRoles(serverId, { userIds: [viewingMember.userId], setRoleIds: roleIds })
              },
            }}
            accountActions={{
              sendInvite: async () => {
                try {
                  const res = await peopleApi.sendInvite(viewingMember.userId)
                  return { ok: true as const, message: res.data.emailSent ? 'Invite sent' : 'Status updated — email may not have been delivered' }
                } catch (e: any) { return { ok: false as const, message: errMessage(e) || 'Failed to send invite' } }
              },
              disable: async () => { await peopleApi.bulkDisable([viewingMember.userId]) },
              enable: async () => { await peopleApi.bulkEnable([viewingMember.userId]) },
              setPassword: async (password: string) => {
                try {
                  const res = await peopleApi.setPassword(viewingMember.userId, password)
                  return { ok: true as const, message: res.data.promoted ? 'Password set — account marked Active' : 'Password set' }
                } catch (e: any) { return { ok: false as const, message: errMessage(e) || 'Failed to set password' } }
              },
            }}
          />
        </aside>
      )}
    </div>
  )
}

/* ── Helpers ──────────────────────────────────────────────────── */

function Select({
  value,
  onChange,
  children,
}: {
  value: string
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-muted ring-1 ring-border text-foreground text-sm rounded px-2 py-1.5 focus:outline-none focus:ring-primary transition"
    >
      {children}
    </select>
  )
}

function BulkButton({
  icon, label, onClick, tone,
}: {
  icon: React.ReactNode
  label: string
  onClick: () => void
  tone?: 'destructive'
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded font-medium transition ${
        tone === 'destructive'
          ? 'bg-rose-500/15 text-rose-500 hover:bg-rose-500/25'
          : 'bg-card text-foreground ring-1 ring-border hover:bg-accent/40'
      }`}
    >
      {icon} {label}
    </button>
  )
}

function BulkDropdown({
  icon, label, options, onPick,
}: {
  icon: React.ReactNode
  label: string
  options: { value: string; label: string }[]
  onPick: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs px-2.5 py-1 rounded bg-card text-foreground ring-1 ring-border hover:bg-accent/40 transition"
      >
        {icon} {label} <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-30 min-w-[180px] max-h-[280px] overflow-y-auto bg-card ring-1 ring-border rounded shadow-lg py-1">
          {options.length === 0 && (
            <div className="px-3 py-1.5 text-xs text-muted-foreground">No options</div>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onPick(opt.value); setOpen(false) }}
              className="w-full text-left px-3 py-1.5 text-xs text-foreground hover:bg-accent/40 transition flex items-center gap-1.5"
            >
              <Check size={10} className="opacity-0" /> {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Avoid an unused-vars warning if every callsite already imports
// UserX from lucide; the row "kick" UI is reserved for a future
// re-add that we don't ship in this slice (kept the import close
// to its eventual usage).
void UserX
void Ban

/** Pull a single, useful sentence out of an axios error. Falls back
 *  through reasonable shapes: server `error` string → server `error`
 *  array (zod) → axios message → empty. Used by every toast so the
 *  user reads "User is already active" rather than "[object Object]"
 *  or a generic header. */
function errMessage(e: any): string {
  const data = e?.response?.data
  if (typeof data?.error === 'string') return data.error
  if (Array.isArray(data?.error) && data.error[0]?.message) {
    return String(data.error[0].message)
  }
  if (typeof e?.message === 'string' && e.message) return e.message
  return ''
}
